import { ClassifierVersion } from '../../types'

// Illustrative model choices for the versioned prompt block. gemini-2.5-flash-lite
// is the default (ClassifierPrompt.model in EN-258). Every version's model must be
// in this list so the selected version's model shows as the picked option.
const MODEL_OPTIONS = [
    'gemini-2.5-flash-lite',
    'gemini-2.5-flash',
    'gemini-2.5-pro',
    'gpt-4o-mini'
]

/**
 * The ClassifierPrompt in EN-258: model, thresholds and prompt are versioned
 * together. Set apart from the details card (accent + version chip) so the
 * versioned scope reads at a glance; editing here forks a new draft.
 *
 * The inputs are keyed on the version number so switching versions in the rail
 * reloads their defaults — an uncontrolled input keeps its old value otherwise.
 */
export default function PromptVersionCard({
    version,
    draftVersionNumber
}: {
    version: ClassifierVersion
    draftVersionNumber: number
}) {
    // The chip reflects the version currently loaded into the card — a draft is
    // being edited, anything else is being viewed (read-only).
    const versionChipLabel =
        version.status === 'draft'
            ? `Editing draft v${version.version}`
            : version.status === 'live'
              ? `Viewing live v${version.version}`
              : `Viewing v${version.version}`

    return (
        <div className="bg-white border border-arwen-200 border-l-4 border-l-arwen-600 rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-arwen-200 bg-arwen-50 rounded-tr-lg">
                <span className="font-semibold text-sm text-arwen-700">Prompt version</span>
                <span className="inline-flex items-center text-[11px] font-semibold text-arwen-700 bg-white border border-arwen-200 rounded-full px-2.5 py-0.5">
                    {versionChipLabel}
                </span>
            </div>
            <div className="p-4 space-y-4">
                <div className="flex gap-4">
                    {/* Model */}
                    <div className="flex-1">
                        <label
                            htmlFor="classifier-model"
                            className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"
                        >
                            Model{' '}
                            <span className="normal-case text-gray-400 font-normal">
                                — versioned
                            </span>
                        </label>
                        <select
                            id="classifier-model"
                            key={`model-${version.version}`}
                            defaultValue={version.model}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans bg-white"
                        >
                            {MODEL_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Default thresholds — one per moderation queue (Borderline, Confirmed),
                        calibrated with the prompt so versioned with it. Confirmed must always
                        exceed Borderline; there is no Safe threshold — it was always
                        meaningless (EN-258). Uncapped: above 1 is how a signal-only
                        classifier never moderates (EN-510). */}
                    <div>
                        <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                            Default thresholds{' '}
                            <span className="normal-case text-gray-400 font-normal">
                                — confirmed &gt; borderline
                            </span>
                        </span>
                        <div className="flex gap-2">
                            <div>
                                <label
                                    htmlFor="classifier-threshold-borderline"
                                    className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-suspect"
                                >
                                    <span
                                        className="h-2 w-2 rounded-full bg-suspect"
                                        aria-hidden="true"
                                    />
                                    Borderline
                                </label>
                                <input
                                    id="classifier-threshold-borderline"
                                    key={`borderline-${version.version}`}
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    defaultValue={version.defaultBorderlineThreshold.toFixed(2)}
                                    className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans tabular-nums"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="classifier-threshold-confirmed"
                                    className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-severe"
                                >
                                    <span
                                        className="h-2 w-2 rounded-full bg-severe"
                                        aria-hidden="true"
                                    />
                                    Confirmed
                                </label>
                                <input
                                    id="classifier-threshold-confirmed"
                                    key={`confirmed-${version.version}`}
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    defaultValue={version.defaultConfirmedThreshold.toFixed(2)}
                                    className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans tabular-nums"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Prompt — shows the selected version's prompt (read-only in this wireframe) */}
                <div>
                    <label
                        htmlFor="classifier-prompt"
                        className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"
                    >
                        Prompt{' '}
                        <span className="normal-case text-gray-400 font-normal">
                            — internal, never shown to clients
                        </span>
                    </label>
                    <textarea
                        id="classifier-prompt"
                        value={version.prompt}
                        readOnly
                        rows={12}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono text-[#243b4a] bg-[#fbfdff] leading-relaxed cursor-default"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100">
                <span className="mr-auto text-[11px] text-gray-400">
                    Editing model, thresholds or prompt creates a new draft version.
                </span>
                <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                    aria-label={`Discard draft v${draftVersionNumber}`}
                >
                    Discard draft
                </button>
                <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                    Test
                </button>
                <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                    Save draft
                </button>
            </div>
        </div>
    )
}
